import { type StudioRequestSubject, type Subject } from '../types';
import { assertExhaustive, getAnalyzedSubjectFromRequestSubject, isRequestSubject, type Nominal } from 'venn-utils';

const subjectKeyPrefix = 'sk#';
type SubjectKeyUnbranded = `${typeof subjectKeyPrefix}${string}`;

export type SubjectKey = Nominal<SubjectKeyUnbranded, 'SubjectKey'>;
export const isSubjectKey = (key: string): key is SubjectKey => key.startsWith(subjectKeyPrefix);

/**
 * Order matches the pre-discriminated-union layout of {@link Subject} (fundId, portfolioId, portfolioVersion,
 * privateFundId, privatePortfolioId). This key is embedded in saved views (as custom metric grid row/column
 * keys), so the string format must stay stable for subjects that were keyed before this type became a union.
 */
const subjectKeyParts = (subject: Subject): (string | number | undefined)[] => {
  switch (subject.type) {
    case 'fund':
      return [subject.fundId, undefined, undefined, undefined, undefined, undefined];
    case 'portfolio':
      return [undefined, subject.portfolioId, subject.portfolioVersion, undefined, undefined, undefined];
    case 'private-fund':
      return [undefined, undefined, undefined, subject.privateFundId, undefined, undefined];
    case 'private-portfolio':
      return [undefined, undefined, undefined, undefined, subject.privatePortfolioId, undefined];
    default:
      return assertExhaustive(subject);
  }
};

/** @returns a branded and prefixed string that allows for deep equality checking given a {@link Subject}. */
export const subjectToKeyString = (subject: Subject | StudioRequestSubject): SubjectKey => {
  const resolvedSubject = isRequestSubject(subject) ? getAnalyzedSubjectFromRequestSubject(subject) : subject;
  return `${subjectKeyPrefix}${subjectKeyParts(resolvedSubject).join('')}` satisfies SubjectKeyUnbranded as SubjectKey;
};
